// TroubleshootPage.js - Quick test component
import React, { useState } from "react";
// import { useAuth } from "../../Utility/AuthProvider";
import { useAuth } from "./AuthProvider";
// import APICall from "../../Utility/APICall";
import APICall from "./APICall";
// import { paths } from "../../Utility/Constants";
import { paths } from "./Constants";
// import { useSettings, useUpdateSettings } from "../../Contexts/SettingsContext";
import { useSettings, useUpdateSettings } from "../Contexts/SettingsContext";

const TroubleshootPage = () => {
  const { user, jwt } = useAuth();
  const settings = useSettings();
  const updateSettings = useUpdateSettings();
  const [testResults, setTestResults] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const runTest = async (testName, testFunction) => {
    setIsLoading(true);
    try {
      const result = await testFunction();
      setTestResults((prev) => ({
        ...prev,
        [testName]: { success: true, result },
      }));
    } catch (error) {
      setTestResults((prev) => ({
        ...prev,
        [testName]: { success: false, error: error.message },
      }));
    }
    setIsLoading(false);
  };

  const testKYCAPI = async () => {
    console.log("🧪 Testing KYC API...");
    const response = await APICall(
      "GET",
      paths.user.kycStatus,
      undefined,
      undefined,
      jwt
    );
    console.log("KYC API Response:", response);
    return response;
  };

  const testUserUpdate = async () => {
    console.log("🧪 Testing User Update API...");
    const testData = { first_name: user?.first_name };
    const response = await APICall(
      "PUT",
      paths.user.update,
      testData,
      undefined,
      jwt
    );
    console.log("User Update API Response:", response);
    return response;
  };

  const testSettings = () => {
    console.log("🧪 Testing Settings...");
    console.log("Current settings:", settings);

    // Test toggle
    const testSetting = "allowHapticFeedback";
    const currentValue = settings?.[testSetting];
    const newValue = !currentValue;

    updateSettings({ [testSetting]: newValue });
    console.log(`Toggled ${testSetting} from ${currentValue} to ${newValue}`);

    return { toggled: testSetting, from: currentValue, to: newValue };
  };

  const testNavigation = () => {
    console.log("🧪 Testing Navigation...");
    console.log("Current path:", window.location.pathname);
    console.log("User object:", user);
    console.log("JWT present:", !!jwt);

    return {
      currentPath: window.location.pathname,
      userPresent: !!user,
      jwtPresent: !!jwt,
    };
  };

  const TestResult = ({ testName, result }) => {
    if (!result) return null;

    return (
      <div
        className={`p-4 rounded-lg mb-4 ${result.success ? "bg-green-100 border border-green-400" : "bg-red-100 border border-red-400"}`}
      >
        <h4
          className={`font-semibold ${result.success ? "text-green-800" : "text-red-800"}`}
        >
          {testName} {result.success ? "✅" : "❌"}
        </h4>
        <pre
          className={`text-sm mt-2 ${result.success ? "text-green-700" : "text-red-700"}`}
        >
          {JSON.stringify(
            result.success ? result.result : result.error,
            null,
            2
          )}
        </pre>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Troubleshooting Dashboard
        </h1>
        <p className="text-gray-600">
          Test various functionalities to identify issues
        </p>
      </div>

      {/* Current State */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Current State</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <strong>User:</strong>{" "}
            {user ? `${user.first_name} ${user.last_name}` : "Not loaded"}
          </div>
          <div>
            <strong>JWT:</strong> {jwt ? "Present" : "Missing"}
          </div>
          <div>
            <strong>User ID:</strong> {user?.entity_id || "N/A"}
          </div>
          <div>
            <strong>Settings:</strong> {settings ? "Loaded" : "Not loaded"}
          </div>
          <div className="md:col-span-2">
            <strong>Current Path:</strong> {window.location.pathname}
          </div>
        </div>
      </div>

      {/* Test Buttons */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Run Tests</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => runTest("KYC API", testKYCAPI)}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            Test KYC API
          </button>

          <button
            onClick={() => runTest("User Update API", testUserUpdate)}
            disabled={isLoading}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            Test User Update
          </button>

          <button
            onClick={() => runTest("Settings", testSettings)}
            disabled={isLoading}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            Test Settings
          </button>

          <button
            onClick={() => runTest("Navigation", testNavigation)}
            disabled={isLoading}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
          >
            Test Navigation
          </button>
        </div>
      </div>

      {/* Test Results */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold mb-4">Test Results</h2>
        {Object.keys(testResults).length === 0 ? (
          <p className="text-gray-500">
            No tests run yet. Click the buttons above to start testing.
          </p>
        ) : (
          Object.entries(testResults).map(([testName, result]) => (
            <TestResult key={testName} testName={testName} result={result} />
          ))
        )}
      </div>

      {/* Quick Links */}
      <div className="bg-white rounded-lg shadow-sm p-6 mt-8">
        <h2 className="text-xl font-semibold mb-4">Quick Navigation</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a
            href="/dashboard/profile"
            className="block px-4 py-2 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 text-center"
          >
            Profile Layout
          </a>
          <a
            href="/dashboard/profile/account"
            className="block px-4 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 text-center"
          >
            Account Page
          </a>
          <a
            href="/dashboard/profile/settings"
            className="block px-4 py-2 bg-purple-100 text-purple-800 rounded-lg hover:bg-purple-200 text-center"
          >
            Settings Page
          </a>
        </div>
      </div>

      {/* Debug Information */}
      <div className="bg-gray-100 rounded-lg p-6 mt-8">
        <h2 className="text-xl font-semibold mb-4">Debug Information</h2>
        <div className="text-sm space-y-2">
          <div>
            <strong>API Paths Available:</strong>
          </div>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>KYC Status: {paths.user?.kycStatus || "Not defined"}</li>
            <li>User Update: {paths.user?.update || "Not defined"}</li>
            <li>User Logout: {paths.user?.logout || "Not defined"}</li>
          </ul>

          <div className="mt-4">
            <strong>Settings Context:</strong>
          </div>
          <pre className="bg-white p-2 rounded text-xs overflow-auto">
            {JSON.stringify(settings, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default TroubleshootPage;
